<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Switch from 'next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import DurationInput from 'next/input/DurationInput.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';

const store = useStore();
const { t } = useI18n();
const { currentAccount } = useAccount();

const conversationAttributes = useMapGetter(
  'attributes/getConversationAttributes'
);
const labels = useMapGetter('labels/getLabels');
const pipelines = computed(() => store.getters['kanban/getPipelines']);
const stagesByPipeline = computed(() => store.state.kanban.stages || {});
const statusOptions = computed(() => [
  {
    key: 'open',
    label: t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.STATUSES.OPEN'),
  },
  {
    key: 'pending',
    label: t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.STATUSES.PENDING'),
  },
  {
    key: 'snoozed',
    label: t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.STATUSES.SNOOZED'),
  },
]);

const duration = ref(null);
const unit = ref(DURATION_UNITS.MINUTES);
const message = ref('');
const ignoreWaiting = ref(false);
const selectedStatuses = ref(['open']);
const selectedRequiredAttributes = ref([]);
const selectedStageIds = ref([]);
const missingAttributesLabel = ref(null);
const autoResolveLabel = ref(null);
const blockOnResolutionSlaBreach = ref(true);
const isSaving = ref(false);

const labelOptions = computed(() =>
  labels.value?.length
    ? labels.value.map(label => ({
        id: label.title,
        name: label.title,
      }))
    : []
);

const attributeOptions = computed(() =>
  (conversationAttributes.value || []).map(attribute => ({
    key: attribute.attributeKey,
    label: attribute.attributeDisplayName,
  }))
);

const pipelineStageGroups = computed(() =>
  pipelines.value.map(pipeline => ({
    ...pipeline,
    stages: stagesByPipeline.value[pipeline.id] || [],
  }))
);

const selectedLabel = labelName =>
  labelOptions.value.find(option => option.name === labelName) || null;

const selectedLabelName = selected => selected?.name || null;

const setUnitFromDuration = value => {
  if (!value) {
    unit.value = DURATION_UNITS.MINUTES;
    return;
  }

  if (value % (24 * 60) === 0) {
    unit.value = DURATION_UNITS.DAYS;
  } else if (value % 60 === 0) {
    unit.value = DURATION_UNITS.HOURS;
  } else {
    unit.value = DURATION_UNITS.MINUTES;
  }
};

const loadSettings = () => {
  const settings = currentAccount.value?.settings || {};
  const rules = settings.auto_resolve_workflow_rules || {};

  duration.value = settings.auto_resolve_after || null;
  message.value = settings.auto_resolve_message || '';
  ignoreWaiting.value = settings.auto_resolve_ignore_waiting || false;
  selectedStatuses.value = rules.statuses?.length ? rules.statuses : ['open'];
  selectedRequiredAttributes.value = rules.required_attributes || [];
  selectedStageIds.value = (rules.allowed_kanban_stage_ids || []).map(Number);
  blockOnResolutionSlaBreach.value =
    rules.block_on_resolution_sla_breach ?? true;
  missingAttributesLabel.value = selectedLabel(rules.missing_attributes_label);
  autoResolveLabel.value = selectedLabel(settings.auto_resolve_label);
  setUnitFromDuration(duration.value);
};

watch([currentAccount, labelOptions], loadSettings, {
  deep: true,
  immediate: true,
});

onMounted(async () => {
  store.dispatch('attributes/get');
  store.dispatch('labels/get');
  await store.dispatch('kanban/fetchPipelines');
  await Promise.all(
    pipelines.value.map(pipeline =>
      store.dispatch('kanban/fetchStages', pipeline.id)
    )
  );
});

const toggleListValue = (listRef, value, enabled) => {
  if (enabled) {
    listRef.value = Array.from(new Set([...listRef.value, value]));
  } else {
    listRef.value = listRef.value.filter(item => item !== value);
  }
};

const toggleStatus = (status, enabled) => {
  if (enabled) {
    selectedStatuses.value = Array.from(
      new Set([...selectedStatuses.value, status])
    );
  } else {
    selectedStatuses.value = selectedStatuses.value.filter(
      item => item !== status
    );
  }
};

const isStatusSelected = status => selectedStatuses.value.includes(status);
const isAttributeSelected = key =>
  selectedRequiredAttributes.value.includes(key);
const isStageSelected = id => selectedStageIds.value.includes(id);

const handleSubmit = async () => {
  if (!duration.value || duration.value < 10) {
    useAlert(t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.DURATION.ERROR'));
    return;
  }

  try {
    isSaving.value = true;
    await store.dispatch('accounts/updateConversationWorkflowSettings', {
      auto_resolve_after: duration.value,
      auto_resolve_message: message.value,
      auto_resolve_ignore_waiting: ignoreWaiting.value,
      auto_resolve_label: selectedLabelName(autoResolveLabel.value),
      auto_resolve_workflow_rules: {
        statuses: selectedStatuses.value,
        required_attributes: selectedRequiredAttributes.value,
        missing_attributes_label: selectedLabelName(
          missingAttributesLabel.value
        ),
        allowed_kanban_stage_ids: selectedStageIds.value,
        allow_without_kanban_deal: true,
        block_on_resolution_sla_breach: blockOnResolutionSlaBreach.value,
      },
    });
    useAlert(t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.SAVE.SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.SAVE.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 px-5 py-4">
      <h3 class="text-heading-2 text-n-slate-12">
        {{ t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.TITLE') }}
      </h3>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.DESCRIPTION') }}
      </p>
    </div>

    <form class="grid gap-5 px-5 py-4" @submit.prevent="handleSubmit">
      <WithLabel
        name="auto-resolve-duration"
        :label="t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.DURATION.LABEL')"
        :help-message="
          t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.DURATION.HELP')
        "
      >
        <div class="flex w-full max-w-lg">
          <DurationInput
            v-model="duration"
            v-model:unit="unit"
            :min="0"
            :max="1438560"
          />
        </div>
      </WithLabel>

      <WithLabel
        name="auto-resolve-message"
        :label="t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.MESSAGE.LABEL')"
        :help-message="
          t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.MESSAGE.HELP')
        "
      >
        <TextArea
          v-model="message"
          class="w-full"
          :placeholder="
            t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.MESSAGE.PLACEHOLDER')
          "
        />
      </WithLabel>

      <WithLabel
        name="auto-resolve-statuses"
        :label="t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.STATUSES.LABEL')"
        :help-message="
          t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.STATUSES.HELP')
        "
      >
        <div class="grid gap-2 sm:grid-cols-3">
          <label
            v-for="status in statusOptions"
            :key="status.key"
            class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          >
            <Checkbox
              :id="`auto-resolve-status-${status.key}`"
              :model-value="isStatusSelected(status.key)"
              @change="toggleStatus(status.key, $event.target.checked)"
            />
            <span>{{ status.label }}</span>
          </label>
        </div>
      </WithLabel>

      <WithLabel
        name="auto-resolve-required-attributes"
        :label="
          t(
            'CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.REQUIRED_ATTRIBUTES.LABEL'
          )
        "
        :help-message="
          t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.REQUIRED_ATTRIBUTES.HELP')
        "
      >
        <div
          v-if="attributeOptions.length"
          class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
        >
          <label
            v-for="attribute in attributeOptions"
            :key="attribute.key"
            class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          >
            <Checkbox
              :id="`auto-resolve-attribute-${attribute.key}`"
              :model-value="isAttributeSelected(attribute.key)"
              @change="
                toggleListValue(
                  selectedRequiredAttributes,
                  attribute.key,
                  $event.target.checked
                )
              "
            />
            <span class="min-w-0 truncate">{{ attribute.label }}</span>
          </label>
        </div>
        <p v-else class="mb-0 text-sm text-n-slate-11">
          {{
            t(
              'CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.REQUIRED_ATTRIBUTES.EMPTY'
            )
          }}
        </p>
      </WithLabel>

      <div class="grid gap-5 lg:grid-cols-2">
        <WithLabel
          name="auto-resolve-missing-label"
          :label="
            t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.MISSING_LABEL.LABEL')
          "
          :help-message="
            t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.MISSING_LABEL.HELP')
          "
        >
          <AdaptiveSelect
            v-model="missingAttributesLabel"
            presentation="compact"
            :options="labelOptions"
            :placeholder="
              t(
                'CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.MISSING_LABEL.PLACEHOLDER'
              )
            "
            placeholder-icon="i-lucide-chevron-down"
            placeholder-trailing-icon
            variant="faded"
          />
        </WithLabel>

        <WithLabel
          name="auto-resolve-resolved-label"
          :label="
            t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.RESOLVED_LABEL.LABEL')
          "
          :help-message="
            t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.RESOLVED_LABEL.HELP')
          "
        >
          <AdaptiveSelect
            v-model="autoResolveLabel"
            presentation="compact"
            :options="labelOptions"
            :placeholder="
              t(
                'CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.RESOLVED_LABEL.PLACEHOLDER'
              )
            "
            placeholder-icon="i-lucide-chevron-down"
            placeholder-trailing-icon
            variant="faded"
          />
        </WithLabel>
      </div>

      <WithLabel
        name="auto-resolve-kanban-stages"
        :label="t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.KANBAN.LABEL')"
        :help-message="
          t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.KANBAN.HELP')
        "
      >
        <div v-if="pipelineStageGroups.length" class="grid gap-3">
          <div
            v-for="pipeline in pipelineStageGroups"
            :key="pipeline.id"
            class="grid gap-2 rounded-lg border border-n-weak bg-n-solid-1 p-3"
          >
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{ pipeline.name }}
            </p>
            <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <label
                v-for="stage in pipeline.stages"
                :key="stage.id"
                class="flex items-center gap-2 text-sm text-n-slate-12"
              >
                <Checkbox
                  :id="`auto-resolve-stage-${stage.id}`"
                  :model-value="isStageSelected(stage.id)"
                  @change="
                    toggleListValue(
                      selectedStageIds,
                      stage.id,
                      $event.target.checked
                    )
                  "
                />
                <span class="min-w-0 truncate">{{ stage.name }}</span>
              </label>
            </div>
          </div>
        </div>
        <p v-else class="mb-0 text-sm text-n-slate-11">
          {{ t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.KANBAN.EMPTY') }}
        </p>
      </WithLabel>

      <div class="grid gap-4 md:grid-cols-2">
        <div class="grid h-full grid-rows-[auto_1fr] gap-1">
          <p class="text-sm font-medium leading-6 text-n-slate-12">
            {{ t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.SLA.LABEL') }}
          </p>
          <div
            class="flex h-full min-h-16 items-center justify-between gap-6 rounded-lg border border-n-weak bg-n-solid-1 px-4 py-3"
          >
            <span class="flex-1 text-sm leading-5 text-n-slate-12">
              {{
                t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.SLA.BLOCK_LABEL')
              }}
            </span>
            <Switch v-model="blockOnResolutionSlaBreach" class="shrink-0" />
          </div>
        </div>

        <div class="grid h-full grid-rows-[auto_1fr] gap-1">
          <p class="text-sm font-medium leading-6 text-n-slate-12">
            {{
              t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.PREFERENCES.LABEL')
            }}
          </p>
          <div
            class="flex h-full min-h-16 items-center justify-between gap-6 rounded-lg border border-n-weak bg-n-solid-1 px-4 py-3"
          >
            <span class="flex-1 text-sm leading-5 text-n-slate-12">
              {{
                t(
                  'CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.PREFERENCES.IGNORE_WAITING'
                )
              }}
            </span>
            <Switch v-model="ignoreWaiting" class="shrink-0" />
          </div>
        </div>
      </div>

      <div class="flex justify-end">
        <Button
          type="submit"
          sm
          icon="i-lucide-save"
          :is-loading="isSaving"
          :label="t('CONVERSATION_WORKFLOW.AUTO_RESOLVE_RULES.SAVE.BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
